import Vue from 'vue';
import Router from 'vue-router';

// NOTE: this will be used on private routes
// import Index from '@/components/general/Index';
import LandingPage from '@/components/general/LandingPage';
import NotFound from '@/components/general/NotFound';
import AccountConfirmation from '@/components/general/AccountConfirmation';
import AccountUnlock from '@/components/general/AccountUnlock';
import Registration from '@/components/auth/registration/Registration';
import Login from '@/components/auth/login/Login';
import NewDeal from '@/components/new_deal/components/NewDeal';
import DealConfirmation from '@/components/new_deal/components/DealConfirmation';
import MyDeals from '@/components/my_deals/deals/MyDeals';
import DealDetail from '@/components/my_deals/deals/DealDetail';
import ProfilePage from '@/components/profile/ProfilePage';
import EntitiesList from '@/components/entities/EntitiesList';
import EntityDetailsPage from '@/components/entities/EntityDetailsPage';
import BorrowerDocsPage from '@/components/documents/BorrowerDocsPage';
import PlaidPage from '@/components/plaid/PlaidPage';
import PaymentMethodsList from '@/components/payments/PaymentMethodsList';
import PdfSigned from '@/components/utils/PdfSigned';
import BorrowerApplicationPage from '@/components/borrower_application/BorrowerApplicationPage';
import Guarantor from '@/components/borrower_application/Guarantor';
import SettingsPage from '@/components/settings/SettingsPage';
import Invitation from '@/components/auth/invitation/Invitation';
import Contributor from '@/components/auth/contributor/Contributor';
import TeamsArea from '@/components/auth/teams_area/TeamsArea';
import {
  APPLICATION_READ, APPLICATION_WRITE,
  DASHBOARD_READ,
  ENTITY_READ,
  LOANS_READ,
  LOANS_WRITE,
  MYDOCS_READ,
  PAYMENTS_READ,
  PLAID_READ,
} from '@/libs/permissions_list';

Vue.use(Router);

const router = new Router({
  routes: [
    {
      path: '/',
      name: 'Index',
      component: LandingPage,
      meta: { requiresUnauth: true },
    },
    {
      path: '/users/:id/verify/:token',
      name: 'AccountConfirmation',
      component: AccountConfirmation,
      props: true,
    },
    {
      path: '/unlock_account',
      name: 'AccountUnlock',
      component: AccountUnlock,
      props: true,
    },
    {
      path: '/registration',
      name: 'Registration',
      component: Registration,
      props: true,
      meta: { requiresUnauth: true, requiresLowerEnv: true },
    },
    {
      path: '/login',
      name: 'Login',
      component: Login,
      meta: { requiresUnauth: true },
    },
    {
      path: '/new_inquiry',
      name: 'Inquiry',
      redirect: () => {
        window.location.href = 'https://www.anchorloans.com/contact';
      },
    },
    {
      path: '/new_deal',
      name: 'NewDeal',
      component: NewDeal,
      props: true,
      meta: {
        requiresAuth: true,
        requiresVerification: true,
        requiresPermission: true,
        allowedPermissions: [LOANS_READ, LOANS_WRITE],
      },
    },
    {
      path: '/deal_confirmation',
      name: 'DealConfirmation',
      component: DealConfirmation,
      props: true,
      meta: {
        requiresPermission: true,
        allowedPermissions: [LOANS_READ, LOANS_WRITE],
      },
    },
    {
      path: '/my_deals',
      name: 'MyDeals',
      component: MyDeals,
      props: route => ({ category: route.query.category }),
      meta: {
        requiresAuth: true,
        requiresPermission: true,
        allowedPermissions: [LOANS_READ],
      },
    },
    {
      path: '/my_deals/:loan_id',
      name: 'DealDetail',
      component: DealDetail,
      props: route => ({ action: route.query.action }),
      meta: {
        requiresAuth: true,
        requiresPermission: true,
        allowedPermissions: [LOANS_READ],
      },
    },
    {
      path: '/dashboard',
      name: 'Dashboard',
      component: ProfilePage,
      meta: {
        requiresAuth: true,
        requiresPermission: true,
        allowedPermissions: [DASHBOARD_READ],
      },
    },
    {
      path: '/home',
      redirect: { name: 'MyDeals' },
    },
    {
      path: '/entities',
      name: 'EntitiesList',
      component: EntitiesList,
      meta: {
        requiresAuth: true,
        requiresPermission: true,
        allowedPermissions: [ENTITY_READ],
      },
    },
    {
      path: '/my_documents',
      name: 'MyDocuments',
      component: BorrowerDocsPage,
      meta: {
        requiresAuth: true,
        requiresPermission: true,
        allowedPermissions: [MYDOCS_READ],
      },
    },
    {
      path: '/entities/:id',
      name: 'EntityDetails',
      component: EntityDetailsPage,
      meta: {
        requiresAuth: true,
        requiresPermission: true,
        allowedPermissions: [ENTITY_READ],
      },
    },
    {
      path: '/payment_methods',
      name: 'PaymentMethodsList',
      component: PaymentMethodsList,
      meta: {
        requiresAuth: true,
        requiresPermission: true,
        allowedPermissions: [PAYMENTS_READ],
      },
    },
    {
      path: '/pdf_signed',
      name: 'PdfSigned',
      component: PdfSigned,
      meta: { requiresAuth: true },
    },
    {
      path: '/plaid',
      name: 'Plaid',
      component: PlaidPage,
      meta: {
        requiresAuth: true,
        requiresPermission: true,
        allowedPermissions: [PLAID_READ],
      },
    },
    {
      path: '/borrower_application',
      name: 'BorrowerApplication',
      component: BorrowerApplicationPage,
      props: route => ({ tab: route.query.tab }),
      meta: {
        requiresAuth: true,
        requiresPermission: true,
        allowedPermissions: [APPLICATION_READ],
      },
    },
    {
      path: '/sign_application',
      name: 'SignApplication',
      component: ProfilePage,
      props: true,
      meta: {
        requiresAuth: true,
        requiresVerification: true,
        requiresPermission: true,
        allowedPermissions: [APPLICATION_READ, APPLICATION_WRITE],
      },
    },
    {
      path: '/settings',
      name: 'Settings',
      component: SettingsPage,
      meta: { requiresAuth: true },
    },
    {
      path: '/invitation',
      name: 'Invitation',
      component: Invitation,
      props: true,
      meta: { requiresUnauth: true },
    },
    {
      path: '/contributor',
      name: 'Contributor',
      component: Contributor,
      props: true,
      meta: { requiresUnauth: true },
    },
    {
      path: '/teams_area',
      name: 'TeamsArea',
      component: TeamsArea,
      props: true,
      meta: { requiresAuth: true },
    },
    {
      path: '/guarantor',
      name: 'Guarantor',
      component: Guarantor,
      props: true,
      meta: { requiresAuth: true },
    },
    {
      /*
       * This should always be the last route defined.
       * as it is used for displaying a 404 page for
       * all non-defined routes.
       */
      path: '*',
      name: 'NotFound',
      component: NotFound,
    },
  ],
});

export default router;



// WEBPACK FOOTER //
// ./src/router/index.js